import { z } from "zod";
import { defineStepConfig, VisibilityPredicateType } from "@erp/workflows";
import { entityOfType } from "../../../utils/system-entities";
import { IntegrationKey, SystemEntity } from "@erp/shared-types";

/**
 * Extract Famous Order Number from Email Subject Step
 *
 * Parses an email subject line to extract a Famous order number.
 * The primary expected format is "ASN - <ORDER NUMBER>" (e.g., "ASN - 123456").
 *
 * This step:
 * - Takes an inbound email entity reference as input
 * - Extracts the subject from the email
 * - Parses the subject to find a Famous order number
 * - Returns the extracted order number for use in downstream steps
 *
 * Supported subject formats (in priority order):
 * - "ASN - 123456" (primary format)
 * - "ASN: 123456", "ASN #123456"
 * - "Order: 123456", "SO-123456", etc.
 * - Standalone numeric sequences (fallback)
 *
 * Note: This is a backend-only step that requires database access.
 */
export const extractFamousOrderFromEmailSubjectStep = defineStepConfig({
  type: "extract_famous_order_from_email_subject",
  name: "Extract Famous Order from Email Subject",
  description:
    "Parse email subject to extract a Famous order number for shipment processing",
  category: "Integration",
  visibility: [
    [
      {
        type: VisibilityPredicateType.Integration,
        integration: IntegrationKey.Famous,
      },
    ],
  ],
  inputSchema: z.object({
    inboundEmail: entityOfType(SystemEntity.INBOUND_EMAIL).describe(
      "The inbound email to extract the Famous order number from",
    ),
  }),

  outputSchema: z.object({
    famousOrderNumber: z
      .string()
      .describe("The extracted Famous order number from the email subject"),
    emailSubject: z
      .string()
      .describe("The original email subject for reference"),
  }),

  configSchema: z.object({
    orderNumberPattern: z
      .string()
      .optional()
      .describe(
        "Optional regex pattern to match the order number. Defaults to matching numeric order IDs.",
      ),
    cancelIfNotFound: z
      .boolean()
      .default(true)
      .describe("Cancel the workflow if no order number is found in subject"),
  }),

  emits: [] as const,
});
